import React, { Fragment, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import get from 'lodash/get'
import set from 'lodash/set'

import { useQuery, useMutation } from 'components/graphql'
import { ContentBlock } from 'components/ContentBlock'
import { Form, Text, Select, Checkbox } from 'components/forms'
import getFAPriceLevels from '../gql/getAppConfigFA'
import updatePriceLevels from '../gql/updatePriceLevels'
import getPriceLevels from 'config/localServer/query/priceLevelsQuery'
import { initQuery } from 'components/withAppInit/withInitQuery'
import { INIT_QUERY_PARAMS } from 'config/consts'
import Divider from 'components/Divider'
import styles from './priceLevels.module.scss'

const PriceLevels = ({ formData, onUpdate, loading, className }) => {
	const [timeoutId, setTimeoutId] = useState(null)
	const [priceLevel, setPriceLevel] = useState(formData.maxPriceLevel)
	useEffect(() => {
		return () => {
			clearTimeout(timeoutId)
		}
	}, [timeoutId])
	const [t] = useTranslation()

	const { data: priceLevelsData = {}, loading: priceLevelsLoading } = useQuery(getPriceLevels, {
		variables: { onlyActive: false, defaultNames: false },
		fetchPolicy: 'cache-and-network'
	})
	const { data: faPriceLevelsData = {} } = useQuery(getFAPriceLevels, {
		fetchPolicy: 'cache-and-network'
	})
	const [updatePriceLevelsMutation] = useMutation(updatePriceLevels)

	formData.priceLevels = priceLevelsData.getClientPriceLevels || []

	const getLevels = ({ priceLevelsLinkFA = [], priceLevelsCafeLinkFA = [], priceLevels = [] }, formApi) => {
		const names = get(faPriceLevelsData, 'getFA_Config.articlePriceCategory')
		const notSelectedOption = { value: -1, label: `<${t('options_section.strings.price_level_null')}>` }
		const options = Array.isArray(names) ?
			names.reduce((ourArr, item, i) => item ? [...ourArr, { value: i + 1, label: item }] : ourArr, [notSelectedOption])
			: []
		const levels = []
		for(let i = 0; i < 10; i++) {
			const getFields = isInCafe => {
				const isUsed = isInCafe ? priceLevelsCafeLinkFA[i] > 0 : priceLevelsLinkFA[i] > 0
				const inCafeProps = {
					noForm: true,
					value: priceLevels[i] || '',
					disabled: true,
					readOnly: true
				}
				return(
					<div>
						<div className={styles['flex-container']}>
							<Text
								field={`priceLevels[${i}]`}
								label={`${t('options_section.sub_sections.price_category')} ${i + 1}${isInCafe ? ` ${t('options_section.fields.inCafe')}` : ''}`}
								{...isInCafe && { ...inCafeProps }}
							/>
							<span
								className={classNames(styles['chain-btn'], { unchained: !isUsed })}
								{...isUsed && { onClick: e => {
									formApi.setValue(`priceLevelsLinkFA[${i}]`, -1)
									clearTimeout(timeoutId)
									setTimeoutId(setTimeout(() => formApi.submitForm(), 2000))
								} }}
							>
								<span className={styles['chain-item']} />
								<span className={styles['chain-item']} />
							</span>
							<Select
								field={`${isInCafe ? 'priceLevelsCafe' : 'priceLevels'}LinkFA[${i}]`}
								label={t('options_section.fields.category_fa')}
								options={options}
								disabled={!!isUsed}
								onChange={() => clearTimeout(timeoutId)} />
						</div>
					</div>
				)
			}
			levels.push(
				<div key={i} style={{ display: (i + 1) <= priceLevel ? '' : 'none' }}>
					<Divider visible />
					<div className={styles['flex-container']}>
						{getFields()}
						<span className={styles['col-divider']} />
						{getFields(true)}
					</div>
				</div>
			)
		}
		return levels
	}
	const getLevelsOptions = () => {
		const out = []
		for(let i = 1; i <= 10; i++) {
			out.push({ value: i, label: i })
		}
		return out
	}
	const handleSubmit = ({ priceLevels, ...vals }) => {
		const isUpdatePriceLevels = priceLevels?.some?.((item, i) => item !== formData?.priceLevels?.[i])
		if(isUpdatePriceLevels) {
			updatePriceLevelsMutation({
				variables: { fields: { priceLevels } },
				update: (cache, { data: updateAppConfigAdditional }) => {
					const cachedData = cache.readQuery({ query: initQuery, variables: INIT_QUERY_PARAMS })
					set(cachedData, 'cashAssist.getAppConfigAdditional.priceLevels', priceLevels)
					cache.writeQuery({
						query: initQuery,
						variables: { defaultLimit: 100 },
						data: cachedData
					})
				}
			})
		}
		return onUpdate(vals)
	}

	return(
		<ContentBlock label={t('options_section.sub_sections.price_category')} className={className}>
			<Form initialValues={formData} onSubmit={handleSubmit} key={priceLevelsLoading} smartSubmit>
				{({ formState: { values }, formApi }) => {
					const formContent = getLevels(values, formApi)
					return(
						<Fragment>
							<Select
								field='maxPriceLevel'
								label={t('options_section.fields.price_levels_number')}
								options={getLevelsOptions()}
								onChange={(option) => setPriceLevel(option.value)}
								validate={() => null} // Removes invalidity after server validation
								validateOnChange
							/>
							<Checkbox field='isExternCreditClient' label={t('options_section.fields.activate_external_credit_customers')} />
							{formContent}
						</Fragment>
					)
				}}
			</Form>
		</ContentBlock>
	)
}

export default PriceLevels
